import { ref, onMounted, watch } from 'vue'

const THEME_STORAGE_KEY = 'preferred-theme'

/**
 * Theme management composable
 * Handles automatic OS detection with manual override capability
 */
export function useTheme() {
  const theme = ref('auto') // 'auto', 'light', or 'dark'
  const actualTheme = ref('light') // 'light' or 'dark' (resolved theme)

  // Detect system preference
  const getSystemTheme = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }

  // Get resolved theme (manual override or system)
  const resolveTheme = (currentTheme) => {
    return currentTheme === 'auto' ? getSystemTheme() : currentTheme
  }

  // Apply theme to document
  const applyTheme = (resolvedTheme) => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolvedTheme)
      actualTheme.value = resolvedTheme
      
      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]')
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', resolvedTheme === 'dark' ? '#1f2937' : '#ffffff')
      }
    }
  }

  // Load theme from localStorage
  const loadTheme = () => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)
      if (savedTheme && ['auto', 'light', 'dark'].includes(savedTheme)) {
        theme.value = savedTheme
      }
    }
  }

  // Save theme to localStorage
  const saveTheme = (newTheme) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    }
  }

  // Set theme with persistence
  const setTheme = (newTheme) => {
    if (['auto', 'light', 'dark'].includes(newTheme)) {
      theme.value = newTheme
      saveTheme(newTheme)
    }
  }

  // Toggle between light and dark (ignoring auto)
  const toggleTheme = () => {
    if (theme.value === 'auto') {
      // If currently auto, toggle based on current resolved theme
      setTheme(actualTheme.value === 'dark' ? 'light' : 'dark')
    } else {
      // If manual override, toggle between light and dark
      setTheme(theme.value === 'dark' ? 'light' : 'dark')
    }
  }

  // Watch for system theme changes when in auto mode
  const setupSystemThemeWatcher = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleChange = () => {
        if (theme.value === 'auto') {
          applyTheme(getSystemTheme())
        }
      }

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange)
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange)
      }
    }
  }

  // Watch theme changes and apply them
  watch(theme, (newTheme) => {
    const resolved = resolveTheme(newTheme)
    applyTheme(resolved)
  }, { immediate: true })

  // Initialize theme system
  onMounted(() => {
    loadTheme()
    applyTheme(resolveTheme(theme.value))
    setupSystemThemeWatcher()
  })

  return {
    theme: theme,           // 'auto', 'light', 'dark'
    actualTheme,           // resolved 'light' or 'dark'
    setTheme,
    toggleTheme,
    isDark: () => actualTheme.value === 'dark',
    isAuto: () => theme.value === 'auto'
  }
}